import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { FiCheck, FiCrop, FiLoader, FiUpload, FiSend } from "react-icons/fi";
import { sendAgentCommand } from "../../agent/agentConnection";
import { COMPANION_APP_LABEL } from "../../agent/constants";
import { parseImageSnipResponse } from "../../utils/agentResponses";
import ImageAnalysisFileList from "./ImageAnalysisFileList";
import ImageAnalysisAnnotatedImage from "./ImageAnalysisAnnotatedImage";
import { BsStars } from "react-icons/bs";
import { useGlobalStore } from "../../context/UserContext/userContext";
import { usePatientAuthFlags } from "../../Hooks/usePatientAuthFlags";
import { useCrePermission } from "../../Hooks/useCrePermission";
import {
  CRE_PERMISSION,
  resolveCreFeatureNotAllowedMessage,
} from "../../cre/crePermissions";
import { useProcessAiModelSpeed } from "../../Hooks/useProcessAiModelSpeed";
import { getUiLanguageLabel } from "../../constants/languages";
import {
  analyzeImageAnalysis,
  followUpImageAnalysis,
  getImageAnalysisContextQuestions,
  uploadImageAnalysisFile,
} from "../../api/imageAnalysisApi";
import { isRasterImageFile } from "./imageAnalysisPreview";

const DICOM_ACCEPTED_EXT = ".dcm,.jpg,.jpeg,.png,.gif,.bmp,.pdf";
const MAX_FILE_MB = 50;

function stripHtmlToText(html) {
  if (!html || typeof html !== "string") return "";
  return html.replace(/<[^>]*>/g, "").replace(/\s+/g, " ").trim();
}

export default function ImageAnalysisPanel({
  studyId,
  onInsertIntoReport,
  language = "en",
}) {
  const { isCreAuth } = usePatientAuthFlags();
  const { requirePermission } = useCrePermission();
  const creSelectedOrganisationId = useGlobalStore(
    (s) => s.creSelectedOrganisationId,
  );
  const [modelSpeed, setModelSpeed] = useProcessAiModelSpeed();
  const { t } = useTranslation("common");
  const languageLabel = getUiLanguageLabel(language);

  const [uploadedImageFileId, setUploadedImageFileId] = useState(null);
  const [uploadedImageFileName, setUploadedImageFileName] = useState("");
  const [localPreviewUrl, setLocalPreviewUrl] = useState(null);
  const filePickerRef = useRef(null);
  const [selectedImageFile, setSelectedImageFile] = useState(null);
  const [captureBusy, setCaptureBusy] = useState(false);

  const [userInputText, setUserInputText] = useState("");
  const [analyzeBusy, setAnalyzeBusy] = useState(false);
  const [analyzeAnswerHtml, setAnalyzeAnswerHtml] = useState("");
  const [analyzeAnswerPlain, setAnalyzeAnswerPlain] = useState("");
  const [findingsImageUrl, setFindingsImageUrl] = useState(null);
  const [addAnnotations, setAddAnnotations] = useState(true);
  const [resultCopied, setResultCopied] = useState(false);
  const [resultInserted, setResultInserted] = useState(false);
  const resultCardRef = useRef(null);
  const copyTickTimeoutRef = useRef(null);
  const insertTickTimeoutRef = useRef(null);
  const topScrollRef = useRef(null);
  const chatScrollRef = useRef(null);

  const [followUpQuestion, setFollowUpQuestion] = useState("");
  const [followUpBusy, setFollowUpBusy] = useState(false);
  const [conversationHistory, setConversationHistory] = useState([]);
  const [uiChatMessages, setUiChatMessages] = useState([]);
  const [contextQuestions, setContextQuestions] = useState([]);
  const [initialSuggestionsLoading, setInitialSuggestionsLoading] =
    useState(false);

  const organisationId = isCreAuth ? creSelectedOrganisationId : null;
  const hasFile = Boolean(uploadedImageFileId || selectedImageFile);
  const isRtlLang = language === "he" || language === "ar";

  const htmlDirectionProps = useMemo(
    () => ({
      dir: isRtlLang ? "rtl" : "ltr",
      style: {
        textAlign: isRtlLang ? "right" : "left",
        direction: isRtlLang ? "rtl" : "ltr",
      },
    }),
    [isRtlLang],
  );

  const updateLocalPreview = useCallback((file) => {
    setLocalPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      if (!file || !isRasterImageFile(file)) return null;
      return URL.createObjectURL(file);
    });
  }, []);

  const handleScreenCapture = useCallback(async () => {
    if (!requirePermission(CRE_PERMISSION.SNIPPING_TOOL)) {
      return;
    }
    setCaptureBusy(true);
    setUploadedImageFileId(null);
    setUploadedImageFileName("");
    setSelectedImageFile(null);
    updateLocalPreview(null);

    try {
      const token = await useGlobalStore.getState().getAccessToken();
      const resp = await sendAgentCommand({ action: "snip" }, token);
      if (!resp) {
        toast.error(
          t("companionAgent.notConnected", { app: COMPANION_APP_LABEL }),
        );
        return;
      }
      const { fileId, previewUrl } = parseImageSnipResponse(resp);

      setUploadedImageFileId(fileId);
      setUploadedImageFileName("screen_capture.jpg");
      if (previewUrl) {
        setLocalPreviewUrl((prev) => {
          if (prev && prev.startsWith("blob:")) URL.revokeObjectURL(prev);
          return previewUrl;
        });
      }
      toast.success(t("companionAgent.captureSuccess"));
    } catch (error) {
      console.error("Image snip failed:", error);
      toast.error(
        error instanceof Error
          ? error.message
          : t("companionAgent.captureFailed"),
      );
    } finally {
      setCaptureBusy(false);
    }
  }, [t, updateLocalPreview, requirePermission]);

  const reset = useCallback(() => {
    setUploadedImageFileId(null);
    setUploadedImageFileName("");
    updateLocalPreview(null);
    setSelectedImageFile(null);
    setUserInputText("");
    setAnalyzeBusy(false);
    setAnalyzeAnswerHtml("");
    setAnalyzeAnswerPlain("");
    setFindingsImageUrl(null);
    setResultCopied(false);
    setResultInserted(false);
    setFollowUpQuestion("");
    setFollowUpBusy(false);
    setConversationHistory([]);
    setUiChatMessages([]);
    setContextQuestions([]);
    setInitialSuggestionsLoading(false);
  }, [updateLocalPreview]);

  const handleAnalyze = useCallback(async () => {
    setAnalyzeBusy(true);

    let imageFileId = uploadedImageFileId;
    try {
      if (!imageFileId) {
        if (!selectedImageFile) {
          filePickerRef.current?.click();
          toast.info(t("imageAnalysis.selectFileToAnalyze"));
          return;
        }
        try {
          const payload = await uploadImageAnalysisFile(selectedImageFile);
          imageFileId = payload.fileId;
          setUploadedImageFileId(payload.fileId);
          setUploadedImageFileName(
            payload.originalFileName || selectedImageFile.name,
          );
          setSelectedImageFile(null);
        } catch (err) {
          console.error(err);
          toast.error(
            resolveCreFeatureNotAllowedMessage(err) ||
              err?.message ||
              t("imageAnalysis.uploadFailed"),
          );
          return;
        }
      }

      if (isCreAuth && !organisationId) {
        toast.error(t("imageAnalysis.organisationRequired"));
        return;
      }

      setAnalyzeAnswerHtml("");
      setAnalyzeAnswerPlain("");
      setFindingsImageUrl(null);
      setConversationHistory([]);
      setUiChatMessages([]);
      setContextQuestions([]);

      const res = await analyzeImageAnalysis({
        uploadedImageFileId: imageFileId,
        studyId,
        organisationId,
        userInputText: String(userInputText || "").trim(),
        language,
        modelSpeed,
        addAnnotations,
      });

      setAnalyzeAnswerHtml(res.answer || "");
      setAnalyzeAnswerPlain(res.rawAnswer || stripHtmlToText(res.answer));
      setFindingsImageUrl(res.findingsImageUrl ?? null);

      setInitialSuggestionsLoading(true);
      void (async () => {
        try {
          const ctx = await getImageAnalysisContextQuestions({
            uploadedImageFileId: imageFileId,
            studyId,
            organisationId,
            userInputText: String(userInputText || "").trim(),
            language,
            modelSpeed,
            question: String(userInputText || "").trim(),
            conversationHistory: [],
          });
          const qs = Array.isArray(ctx?.questions) ? ctx.questions : [];
          setContextQuestions(qs);
        } catch {
          // suggestions are optional
        } finally {
          setInitialSuggestionsLoading(false);
        }
      })();
    } catch (err) {
      console.error(err);
      toast.error(
        resolveCreFeatureNotAllowedMessage(err) ||
          err?.message ||
          t("imageAnalysis.analysisFailed"),
      );
    } finally {
      setAnalyzeBusy(false);
    }
  }, [
    uploadedImageFileId,
    selectedImageFile,
    userInputText,
    studyId,
    isCreAuth,
    organisationId,
    language,
    modelSpeed,
    addAnnotations,
    t,
  ]);

  useEffect(() => {
    if (!analyzeAnswerHtml) return;
    const container = topScrollRef.current;
    const card = resultCardRef.current;
    if (!container || !card) return;

    const containerRect = container.getBoundingClientRect();
    const cardRect = card.getBoundingClientRect();
    const padding = 8;

    const isVisible =
      cardRect.top >= containerRect.top + padding &&
      cardRect.bottom <= containerRect.bottom - padding;
    if (isVisible) return;

    const deltaTop = cardRect.top - containerRect.top - padding;
    if (!Number.isFinite(deltaTop) || deltaTop === 0) return;

    container.scrollTo({
      top: container.scrollTop + deltaTop,
      behavior: "smooth",
    });
  }, [analyzeAnswerHtml]);

  useEffect(() => {
    setResultCopied(false);
    setResultInserted(false);
    if (copyTickTimeoutRef.current) {
      clearTimeout(copyTickTimeoutRef.current);
      copyTickTimeoutRef.current = null;
    }
    if (insertTickTimeoutRef.current) {
      clearTimeout(insertTickTimeoutRef.current);
      insertTickTimeoutRef.current = null;
    }
  }, [analyzeAnswerHtml]);

  useEffect(() => {
    const el = chatScrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [uiChatMessages, followUpBusy]);

  const handleCopyResult = useCallback(async () => {
    if (!analyzeAnswerPlain) return;
    try {
      await navigator.clipboard.writeText(analyzeAnswerPlain);
      setResultCopied(true);
      if (copyTickTimeoutRef.current) clearTimeout(copyTickTimeoutRef.current);
      copyTickTimeoutRef.current = setTimeout(() => {
        setResultCopied(false);
        copyTickTimeoutRef.current = null;
      }, 2000);
    } catch {
      setResultCopied(false);
    }
  }, [analyzeAnswerPlain]);

  const handleInsertResult = useCallback(() => {
    if (!analyzeAnswerHtml || typeof onInsertIntoReport !== "function") return;
    onInsertIntoReport(analyzeAnswerHtml);
    setResultInserted(true);
    if (insertTickTimeoutRef.current) clearTimeout(insertTickTimeoutRef.current);
    insertTickTimeoutRef.current = setTimeout(() => {
      setResultInserted(false);
      insertTickTimeoutRef.current = null;
    }, 2000);
  }, [analyzeAnswerHtml, onInsertIntoReport]);

  const sendFollowUpQuestion = useCallback(
    async (questionRaw) => {
      const question = String(questionRaw || "").trim();
      if (!question || followUpBusy) return;
      if (!uploadedImageFileId) {
        toast.info(t("imageAnalysis.uploadFileFirst"));
        return;
      }
      if (isCreAuth && !organisationId) {
        toast.error(t("imageAnalysis.organisationRequired"));
        return;
      }

      const pendingId = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
      setUiChatMessages((prev) => [
        ...prev,
        { id: pendingId, userQuestion: question, answerHtml: "", pending: true },
      ]);
      setFollowUpBusy(true);

      const apiConversationSnapshot = conversationHistory.slice();
      try {
        const followUpRes = await followUpImageAnalysis({
          uploadedImageFileId,
          studyId,
          organisationId,
          userInputText: String(userInputText || "").trim(),
          language,
          modelSpeed,
          question,
          conversationHistory: apiConversationSnapshot,
        });

        const answerHtml = followUpRes?.answer || "";
        const answerRaw = followUpRes?.rawAnswer || stripHtmlToText(answerHtml);

        setConversationHistory((prev) => [
          ...prev,
          { role: "user", content: question },
          { role: "assistant", content: answerRaw },
        ]);

        setUiChatMessages((prev) =>
          prev.map((m) =>
            m?.id === pendingId
              ? { ...m, answerHtml, pending: false }
              : m,
          ),
        );

        setContextQuestions(
          Array.isArray(followUpRes?.questions) ? followUpRes.questions : [],
        );
        setFollowUpQuestion("");
      } catch (err) {
        console.error(err);
        toast.error(
          resolveCreFeatureNotAllowedMessage(err) ||
            err?.message ||
            t("imageAnalysis.followUpFailed"),
        );
        setUiChatMessages((prev) =>
          prev.map((m) =>
            m?.id === pendingId
              ? {
                  ...m,
                  answerHtml: err?.message || t("imageAnalysis.followUpFailed"),
                  pending: false,
                  isError: true,
                }
              : m,
          ),
        );
      } finally {
        setFollowUpBusy(false);
      }
    },
    [
      uploadedImageFileId,
      userInputText,
      isCreAuth,
      organisationId,
      studyId,
      conversationHistory,
      language,
      modelSpeed,
      followUpBusy,
      t,
    ],
  );

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <div className="flex-1 min-h-0 flex flex-col">
        <div
          ref={topScrollRef}
          className="flex-1 min-h-0 p-4 overflow-y-auto space-y-4"
        >
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <BsStars
                className="text-[#A4C639] dark:text-[#CBEA7B]"
                size={18}
              />
              <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                {t("imageAnalysis.title")}
              </h3>
            </div>
            <div className="flex items-center gap-2">
              {isCreAuth && (
                <button
                  type="button"
                  onClick={() => void handleScreenCapture()}
                  disabled={analyzeBusy || followUpBusy || captureBusy}
                  className="border bg-gray-300 dark:bg-gray-600 p-2 rounded-full flex items-center justify-center w-8 h-8 hover:bg-gray-400 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  title={t("companionAgent.screenCapture")}
                  aria-label={t("companionAgent.screenCapture")}
                >
                  {captureBusy ? (
                    <FiLoader className="w-3.5 h-3.5 animate-spin text-gray-900 dark:text-gray-100" />
                  ) : (
                    <FiCrop className="w-3.5 h-3.5 text-gray-900 dark:text-gray-100" />
                  )}
                </button>
              )}
              {hasFile && (
                <button
                  type="button"
                  onClick={reset}
                  className="text-xs text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 disabled:opacity-50"
                  disabled={analyzeBusy || followUpBusy}
                  title={t("imageAnalysis.clearAll")}
                >
                  {t("imageAnalysis.clearAll")}
                </button>
              )}
            </div>
          </div>

          <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">
            <div className="flex items-center justify-between mb-2 gap-2">
              <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 dark:text-gray-200">
                <FiUpload />
                {t("imageAnalysis.upload")}
              </label>

              <input
                ref={filePickerRef}
                type="file"
                accept={DICOM_ACCEPTED_EXT}
                className="hidden"
                disabled={analyzeBusy || followUpBusy}
                onChange={(e) => {
                  const fileList = e.target.files;
                  if (fileList && fileList.length > 1) {
                    toast.info(t("imageAnalysis.singleFileOnly"));
                  }
                  const file = fileList?.[0] ?? null;
                  e.target.value = "";
                  setSelectedImageFile(file);
                  setUploadedImageFileId(null);
                  setUploadedImageFileName("");
                  updateLocalPreview(file);
                }}
              />

              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => filePickerRef.current?.click()}
                  disabled={analyzeBusy || followUpBusy || captureBusy}
                  className="text-xs px-2.5 py-1 rounded-md bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-100 hover:bg-gray-300 dark:hover:bg-gray-500 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
                >
                  <FiUpload className="w-3.5 h-3.5" />
                  {t("imageAnalysis.uploadFile")}
                </button>
              </div>
            </div>

            <ImageAnalysisFileList
              selectedImageFile={selectedImageFile}
              uploadedImageFileId={uploadedImageFileId}
              uploadedImageFileName={uploadedImageFileName}
              previewUrl={localPreviewUrl}
              onRemove={reset}
              disabled={analyzeBusy || followUpBusy}
            />

            <div className="mt-1.5 text-[11px] text-gray-400 dark:text-gray-500">
              {t("imageAnalysis.acceptedFiles", {
                extensions: DICOM_ACCEPTED_EXT,
                max: MAX_FILE_MB,
              })}
            </div>
          </div>

          <div
            className={`p-3 border rounded-lg ${
              hasFile
                ? "bg-gray-50 dark:bg-gray-700/40 border-gray-200 dark:border-gray-600"
                : "bg-gray-50/30 dark:bg-gray-700/20 border-gray-200/60 dark:border-gray-600/60"
            }`}
          >
            <div className="flex items-center justify-between gap-2 mb-3">
              <div className="flex items-center gap-2 text-xs font-semibold text-gray-700 dark:text-gray-200">
                <BsStars className="text-[#A4C639] dark:text-[#CBEA7B]" size={14} />
                {t("imageAnalysis.analyze")}
              </div>
              <div className="flex items-center gap-2">
                <div className="text-[11px] text-gray-500 dark:text-gray-400 whitespace-nowrap">
                  {t("imageAnalysis.outputLanguage", { language: languageLabel })}
                </div>
                <button
                  type="button"
                  onClick={handleAnalyze}
                  disabled={!hasFile || analyzeBusy}
                  className="px-4 py-2 bg-[#A4C639] hover:bg-[#A4C639]/90 text-gray-900 rounded-lg text-xs font-semibold inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {analyzeBusy ? (
                    <FiLoader className="animate-spin" />
                  ) : (
                    t("imageAnalysis.analyze")
                  )}
                </button>
              </div>
            </div>

            {hasFile && (
              <div className="mb-3 space-y-3">
                <div className="min-w-0">
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                    {t("modelSpeed")}
                  </label>
                  <select
                    value={modelSpeed}
                    onChange={(e) => setModelSpeed(e.target.value)}
                    disabled={analyzeBusy || followUpBusy}
                    className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
                    aria-label={t("modelSpeed")}
                  >
                    <option value="Smarter">{t("modelSpeedSmarter")}</option>
                    <option value="Fastest">{t("modelSpeedFastest")}</option>
                  </select>
                </div>
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={addAnnotations}
                    onChange={(e) => setAddAnnotations(e.target.checked)}
                    disabled={analyzeBusy || followUpBusy}
                    className="mt-0.5 rounded border-gray-300 dark:border-gray-600 text-[#A4C639] focus:ring-[#A4C639] dark:focus:ring-[#CBEA7B]"
                  />
                  <span className="min-w-0">
                    <span className="block text-xs font-medium text-gray-700 dark:text-gray-200">
                      {t("imageAnalysis.addAnnotations")}
                    </span>
                    <span className="block text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">
                      {t("imageAnalysis.addAnnotationsHint")}
                    </span>
                  </span>
                </label>
              </div>
            )}

            <textarea
              value={userInputText}
              onChange={(e) => setUserInputText(e.target.value)}
              placeholder={t("imageAnalysis.contextPlaceholder")}
              rows={2}
              className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 resize-y focus:outline-none focus:ring-2 focus:ring-[#A4C639] dark:focus:ring-[#CBEA7B] focus:border-transparent transition-all text-sm"
              disabled={!hasFile || analyzeBusy}
              dir={isRtlLang ? "rtl" : "ltr"}
            />
          </div>

          {!analyzeAnswerHtml && !analyzeBusy && (
            <p
              className="text-sm text-gray-500 dark:text-gray-400 py-2"
              dir={htmlDirectionProps.dir}
              style={{ textAlign: htmlDirectionProps.style.textAlign }}
            >
              {t("imageAnalysis.emptyResult")}
            </p>
          )}
          {analyzeBusy && !analyzeAnswerHtml && (
            <div
              className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 py-2"
              dir={htmlDirectionProps.dir}
              style={{ direction: htmlDirectionProps.style.direction }}
            >
              <FiLoader className="animate-spin" />
              {t("imageAnalysis.processing")}
            </div>
          )}

          {analyzeAnswerHtml ? (
            <div
              ref={resultCardRef}
              className="p-3 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800"
              dir={htmlDirectionProps.dir}
            >
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="text-xs font-semibold text-gray-700 dark:text-gray-200">
                  {t("imageAnalysis.aiResult")}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleCopyResult}
                    className="px-3 py-1.5 rounded-full border border-gray-300 dark:border-gray-600 bg-transparent text-gray-200 dark:text-gray-100 text-xs hover:bg-gray-100/10 disabled:opacity-50 inline-flex items-center justify-center min-w-[4.5rem]"
                    disabled={!analyzeAnswerPlain}
                    title={resultCopied ? t("imageAnalysis.copied") : t("imageAnalysis.copy")}
                  >
                    {resultCopied ? (
                      <FiCheck className="w-4 h-4" aria-hidden />
                    ) : (
                      t("imageAnalysis.copy")
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={handleInsertResult}
                    disabled={!onInsertIntoReport || !analyzeAnswerHtml}
                    title={
                      resultInserted
                        ? t("imageAnalysis.inserted")
                        : t("imageAnalysis.insertToReport")
                    }
                    className="px-3 py-1.5 rounded-full bg-[#A4C639] dark:bg-[#CBEA7B] text-gray-900 dark:text-black text-xs font-semibold hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center min-w-[7.5rem]"
                  >
                    {resultInserted ? (
                      <FiCheck className="w-4 h-4" aria-hidden />
                    ) : (
                      t("imageAnalysis.insertToReport")
                    )}
                  </button>
                </div>
              </div>

              <div
                className="prose dark:prose-invert max-w-full prose-headings:text-gray-900 dark:prose-headings:text-gray-100 prose-p:text-gray-700 dark:prose-p:text-gray-300 prose-sm"
                dir={htmlDirectionProps.dir}
                style={htmlDirectionProps.style}
                dangerouslySetInnerHTML={{ __html: analyzeAnswerHtml }}
              />
              <ImageAnalysisAnnotatedImage
                imageUrl={findingsImageUrl}
                isRtl={isRtlLang}
              />
            </div>
          ) : null}
        </div>

        <div className="border-t dark:border-gray-700 flex flex-col h-[48vh] xl:h-[52%] min-h-[300px]">
          <div className="flex items-center gap-2 px-4 py-2 border-b dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              {t("imageAnalysis.followUp")}
            </h3>
          </div>

          <div
            ref={chatScrollRef}
            className="flex-1 min-h-0 overflow-y-auto px-4 py-3 space-y-3"
          >
            {!analyzeAnswerHtml && (
              <div
                className="text-xs text-gray-500 dark:text-gray-400"
                dir={htmlDirectionProps.dir}
                style={{ textAlign: htmlDirectionProps.style.textAlign }}
              >
                {t("imageAnalysis.followUpEmpty")}
              </div>
            )}

            {uiChatMessages.map((m, idx) => (
              <div key={m?.id || `${idx}-${m.userQuestion}`} className="space-y-2">
                <div className="flex justify-end">
                  <div
                    dir={isRtlLang ? "rtl" : "ltr"}
                    className="max-w-[85%] px-3 py-2 rounded-2xl text-sm whitespace-pre-wrap break-words bg-[#A4C639] dark:bg-[#CBEA7B] text-gray-900 dark:text-black rounded-br-sm"
                  >
                    {m.userQuestion}
                  </div>
                </div>

                <div className="flex justify-start">
                  <div
                    className={`max-w-[85%] px-3 py-2 rounded-2xl text-sm whitespace-pre-wrap break-words rounded-bl-sm ${
                      m.isError
                        ? "bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200"
                        : "bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-100"
                    }`}
                    dir={isRtlLang ? "rtl" : "ltr"}
                  >
                    {m.pending ? (
                      <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-300">
                        <FiLoader className="animate-spin" />
                        {t("imageAnalysis.thinking")}
                      </div>
                    ) : m.answerHtml ? (
                      <div
                        className="prose dark:prose-invert prose-sm max-w-none prose-p:text-gray-800 dark:prose-p:text-gray-100"
                        dir={htmlDirectionProps.dir}
                        style={htmlDirectionProps.style}
                        dangerouslySetInnerHTML={{ __html: m.answerHtml }}
                      />
                    ) : (
                      <div className="text-xs text-gray-500 dark:text-gray-300">
                        —
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}

            {analyzeAnswerHtml && (
              <div className="pt-2">
                {!followUpBusy && contextQuestions.length > 0 ? (
                  <div
                    className="flex flex-wrap gap-1.5 justify-start"
                    dir={htmlDirectionProps.dir}
                  >
                    {contextQuestions.map((q) => (
                      <button
                        key={q}
                        type="button"
                        onClick={() => sendFollowUpQuestion(q)}
                        disabled={followUpBusy}
                        className="px-2 py-1 rounded-full bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 disabled:opacity-50 text-xs text-start"
                        title={q}
                      >
                        {q}
                      </button>
                    ))}
                  </div>
                ) : (
                  <div
                    className="text-xs text-gray-500 dark:text-gray-400"
                    dir={htmlDirectionProps.dir}
                    style={{ textAlign: htmlDirectionProps.style.textAlign }}
                  >
                    {followUpBusy ? null : initialSuggestionsLoading ? (
                      <span className="inline-flex items-center gap-2">
                        <FiLoader className="animate-spin" />
                        {t("imageAnalysis.loading")}
                      </span>
                    ) : (
                      t("imageAnalysis.noSuggestions")
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              void sendFollowUpQuestion(followUpQuestion);
            }}
            className="p-3 border-t dark:border-gray-700 flex items-center gap-2"
          >
            <input
              type="text"
              value={followUpQuestion}
              onChange={(e) => setFollowUpQuestion(e.target.value)}
              placeholder={t("imageAnalysis.followUpPlaceholder")}
              className="flex-1 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-[#A4C639] dark:focus:ring-[#CBEA7B] focus:border-transparent"
              disabled={!analyzeAnswerHtml || followUpBusy}
              dir={isRtlLang ? "rtl" : "ltr"}
            />
            <button
              type="submit"
              disabled={
                !analyzeAnswerHtml ||
                followUpBusy ||
                !followUpQuestion.trim()
              }
              className="px-3 py-2 rounded-lg bg-[#A4C639] dark:bg-[#CBEA7B] text-gray-900 dark:text-black font-medium hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
              title={t("imageAnalysis.sendFollowUp")}
            >
              <FiSend className="w-4 h-4" />
              <span className="hidden sm:inline">{t("imageAnalysis.send")}</span>
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
